/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

/**
 * Enum derived from https://github.com/microsoft/vscode/blob/main/src/vs/base/common/keyCodes.ts
 *
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License. See License.txt in the project root for license information.
 */

export enum KEY_CODE {
	/**
	 * Placed first to cover the 0 value of the enum.
	 */
	Unknown = 0,

	Backspace = 1,
	Tab = 2,
	Enter = 3,
	Shift = 4,
	Ctrl = 5,
	Alt = 6,
	PauseBreak = 7,
	CapsLock = 8,
	Escape = 9,
	Space = 10,
	PageUp = 11,
	PageDown = 12,
	End = 13,
	Home = 14,
	LeftArrow = 15,
	UpArrow = 16,
	RightArrow = 17,
	DownArrow = 18,
	Insert = 19,
	Delete = 20,

	KEY_0 = 21,
	KEY_1 = 22,
	KEY_2 = 23,
	KEY_3 = 24,
	KEY_4 = 25,
	KEY_5 = 26,
	KEY_6 = 27,
	KEY_7 = 28,
	KEY_8 = 29,
	KEY_9 = 30,

	KEY_A = 31,
	KEY_B = 32,
	KEY_C = 33,
	KEY_D = 34,
	KEY_E = 35,
	KEY_F = 36,
	KEY_G = 37,
	KEY_H = 38,
	KEY_I = 39,
	KEY_J = 40,
	KEY_K = 41,
	KEY_L = 42,
	KEY_M = 43,
	KEY_N = 44,
	KEY_O = 45,
	KEY_P = 46,
	KEY_Q = 47,
	KEY_R = 48,
	KEY_S = 49,
	KEY_T = 50,
	KEY_U = 51,
	KEY_V = 52,
	KEY_W = 53,
	KEY_X = 54,
	KEY_Y = 55,
	KEY_Z = 56,

	Meta = 57,
	ContextMenu = 58,

	F1 = 59,
	F2 = 60,
	F3 = 61,
	F4 = 62,
	F5 = 63,
	F6 = 64,
	F7 = 65,
	F8 = 66,
	F9 = 67,
	F10 = 68,
	F11 = 69,
	F12 = 70,
	F13 = 71,
	F14 = 72,
	F15 = 73,
	F16 = 74,
	F17 = 75,
	F18 = 76,
	F19 = 77,

	NumLock = 78,
	ScrollLock = 79,

	Backquote = 100,
	Minus = 101,
	Equal = 102,
	BracketLeft = 103,
	BracketRight = 104,
	Semicolon = 105,
	Quote = 106,
	Backslash = 107,
	IntlBackslash = 108,
	Comma = 109,
	Period = 110,
	Slash = 111,

	/**
	 * Placed last to cover the length of the enum.
	 * Please do not depend on this value!
	 */
	MAX_VALUE
}

export interface IKeyMap {
	keyCodeToStr: { [K: number]: string };
	keyCodeToStrWin: { [K: number]: string };
	keyCodeToStrLinux: { [K: number]: string };
	keyCodeToStrMac: { [K: number]: string };
	strToKeyCode: { [K: string]: KEY_CODE };
	evCodeToKeyCode: { [K: string]: KEY_CODE };
	evKeyToKeyCode: { [K: string]: KEY_CODE };
}

export interface IKeyMapEntry {
	/** Key code enum */
	code: KEY_CODE;
	/** Event key code (returned by browser) */
	keyCode: string;
	/** User-friendly label */
	label: string;
}

export const keyMap: IKeyMap = {
	keyCodeToStr: {},
	keyCodeToStrWin: {},
	keyCodeToStrLinux: {},
	keyCodeToStrMac: {},
	strToKeyCode: {},
	evCodeToKeyCode: {},
	evKeyToKeyCode: {}
};

(() => {
	function define(
		keyCode: KEY_CODE,
		eventCode: string,
		eventKey: string,
		label: string,
		labelWin?: string,
		labelLinux?: string,
		labelMac?: string
	) {
		keyMap.keyCodeToStr[keyCode] = label;
		keyMap.strToKeyCode[label] = keyCode;
		keyMap.evCodeToKeyCode[eventCode] = keyCode;

		if (eventKey) {
			keyMap.evKeyToKeyCode[eventKey] = keyCode;
		}

		if (labelWin) {
			keyMap.keyCodeToStrWin[keyCode] = labelWin;
		}

		if (labelLinux) {
			keyMap.keyCodeToStrLinux[keyCode] = labelLinux;
		}

		if (labelMac) {
			keyMap.keyCodeToStrMac[keyCode] = labelMac;
		}
	}

	// Key code						Ev.code				Ev.key	Labels (generic, win, linux, mac)
	define(KEY_CODE.Unknown, "unknown", null, "unknown");

	define(KEY_CODE.Backspace, "Backspace", null, "Backspace");
	define(KEY_CODE.Tab, "Tab", null, "Tab");
	define(KEY_CODE.Enter, "Enter", null, "Enter", "↵", "↵", "↵");
	define(KEY_CODE.Shift, "ShiftLeft", null, "ShiftLeft", "L⇧", "L⇧", "L⇧");
	define(KEY_CODE.Shift, "ShiftRight", null, "ShiftRight", "R⇧", "R⇧", "R⇧");
	define(KEY_CODE.Shift, "Shift", null, "Shift", "⇧", "⇧", "⇧");
	define(KEY_CODE.Ctrl, "ControlLeft", null, "Ctrl");
	define(KEY_CODE.Ctrl, "ControlRight", null, "Ctrl");
	define(KEY_CODE.Ctrl, "Control", null, "Ctrl");
	define(KEY_CODE.Alt, "AltLeft", null, "Alt", "Alt", "Alt", "⌥");
	define(KEY_CODE.Alt, "AltRight", null, "Alt", "Alt", "Alt", "⌥");
	define(KEY_CODE.Alt, "Alt", null, "Alt", "Alt", "Alt", "⌥");
	define(KEY_CODE.PauseBreak, "PauseBreak", null, "PauseBreak");
	define(KEY_CODE.CapsLock, "CapsLock", null, "CapsLock");
	define(KEY_CODE.Escape, "Escape", null, "Escape");
	define(KEY_CODE.Space, "Space", null, "Space");
	define(KEY_CODE.PageUp, "PageUp", null, "PageUp");
	define(KEY_CODE.PageDown, "PageDown", null, "PageDown");
	define(KEY_CODE.End, "End", null, "End");
	define(KEY_CODE.Home, "Home", null, "Home");

	define(KEY_CODE.LeftArrow, "ArrowLeft", null, "Left", "←", "←", "←");
	define(KEY_CODE.UpArrow, "ArrowUp", null, "Up", "↑", "↑", "↑");
	define(KEY_CODE.RightArrow, "ArrowRight", null, "Right", "→", "→", "→");
	define(KEY_CODE.DownArrow, "ArrowDown", null, "Down", "↓", "↓", "↓");
	define(KEY_CODE.Insert, "Insert", null, "Insert");
	define(KEY_CODE.Delete, "Delete", null, "Delete");

	define(KEY_CODE.KEY_0, "Digit0", "0", "0");
	define(KEY_CODE.KEY_1, "Digit1", "1", "1");
	define(KEY_CODE.KEY_2, "Digit2", "2", "2");
	define(KEY_CODE.KEY_3, "Digit3", "3", "3");
	define(KEY_CODE.KEY_4, "Digit4", "4", "4");
	define(KEY_CODE.KEY_5, "Digit5", "5", "5");
	define(KEY_CODE.KEY_6, "Digit6", "6", "6");
	define(KEY_CODE.KEY_7, "Digit7", "7", "7");
	define(KEY_CODE.KEY_8, "Digit8", "8", "8");
	define(KEY_CODE.KEY_9, "Digit9", "9", "9");

	define(KEY_CODE.KEY_A, "KeyA", "A", "A");
	define(KEY_CODE.KEY_B, "KeyB", "B", "B");
	define(KEY_CODE.KEY_C, "KeyC", "C", "C");
	define(KEY_CODE.KEY_D, "KeyD", "D", "D");
	define(KEY_CODE.KEY_E, "KeyE", "E", "E");
	define(KEY_CODE.KEY_F, "KeyF", "F", "F");
	define(KEY_CODE.KEY_G, "KeyG", "G", "G");
	define(KEY_CODE.KEY_H, "KeyH", "H", "H");
	define(KEY_CODE.KEY_I, "KeyI", "I", "I");
	define(KEY_CODE.KEY_J, "KeyJ", "J", "J");
	define(KEY_CODE.KEY_K, "KeyK", "K", "K");
	define(KEY_CODE.KEY_L, "KeyL", "L", "L");
	define(KEY_CODE.KEY_M, "KeyM", "M", "M");
	define(KEY_CODE.KEY_N, "KeyN", "N", "N");
	define(KEY_CODE.KEY_O, "KeyO", "O", "O");
	define(KEY_CODE.KEY_P, "KeyP", "P", "P");
	define(KEY_CODE.KEY_Q, "KeyQ", "Q", "Q");
	define(KEY_CODE.KEY_R, "KeyR", "R", "R");
	define(KEY_CODE.KEY_S, "KeyS", "S", "S");
	define(KEY_CODE.KEY_T, "KeyT", "T", "T");
	define(KEY_CODE.KEY_U, "KeyU", "U", "U");
	define(KEY_CODE.KEY_V, "KeyV", "V", "V");
	define(KEY_CODE.KEY_W, "KeyW", "W", "W");
	define(KEY_CODE.KEY_X, "KeyX", "X", "X");
	define(KEY_CODE.KEY_Y, "KeyY", "Y", "Y");
	define(KEY_CODE.KEY_Z, "KeyZ", "Z", "Z");

	define(KEY_CODE.Meta, "MetaLeft", null, "Meta", "❖", "◆", "⌘");
	define(KEY_CODE.Meta, "MetaRight", null, "Meta", "❖", "◆", "⌘");
	define(KEY_CODE.Meta, "Meta", null, "Meta", "❖", "◆", "⌘");
	define(KEY_CODE.ContextMenu, "ContextMenu", null, "ContextMenu");

	define(KEY_CODE.F1, "F1", null, "F1");
	define(KEY_CODE.F2, "F2", null, "F2");
	define(KEY_CODE.F3, "F3", null, "F3");
	define(KEY_CODE.F4, "F4", null, "F4");
	define(KEY_CODE.F5, "F5", null, "F5");
	define(KEY_CODE.F6, "F6", null, "F6");
	define(KEY_CODE.F7, "F7", null, "F7");
	define(KEY_CODE.F8, "F8", null, "F8");
	define(KEY_CODE.F9, "F9", null, "F9");
	define(KEY_CODE.F10, "F10", null, "F10");
	define(KEY_CODE.F11, "F11", null, "F11");
	define(KEY_CODE.F12, "F12", null, "F12");
	define(KEY_CODE.F13, "F13", null, "F13");
	define(KEY_CODE.F14, "F14", null, "F14");
	define(KEY_CODE.F15, "F15", null, "F15");
	define(KEY_CODE.F16, "F16", null, "F16");
	define(KEY_CODE.F17, "F17", null, "F17");
	define(KEY_CODE.F18, "F18", null, "F18");
	define(KEY_CODE.F19, "F19", null, "F19");

	define(KEY_CODE.NumLock, "NumLock", null, "NumLock");
	define(KEY_CODE.ScrollLock, "ScrollLock", null, "ScrollLock");

	define(KEY_CODE.Backquote, "Backquote", null, "Backquote");
	define(KEY_CODE.Minus, "Minus", null, "Minus", "-", "-", "-");
	define(KEY_CODE.Equal, "Equal", null, "Equal", "=", "=", "=");
	define(KEY_CODE.BracketLeft, "BracketLeft", null, "BracketLeft", "[", "[", "[");
	define(KEY_CODE.BracketRight, "BracketRight", null, "BracketRight", "]", "]", "]");
	define(KEY_CODE.Semicolon, "Semicolon", null, ";", ";", ";", ";");
	define(KEY_CODE.Quote, "Quote", null, "Quote");
	define(KEY_CODE.Backslash, "Backslash", null, "Backslash", "`", "`", "`");
	define(KEY_CODE.IntlBackslash, "IntlBackslash", null, "IntlBackslash", "\\", "\\", "\\");
	define(KEY_CODE.Comma, "Comma", null, "Comma", ",", ",", ",");
	define(KEY_CODE.Period, "Period", null, "Period", ".", ".", ".");
	define(KEY_CODE.Slash, "Slash", null, "Slash", "-", "-", "-");
})();
